import React, { useState } from "react";
import ErrorRow from "Common/ErrorRow";
import BreadCrumb from "Common/BreadCrumb";
import { UserCheck, Search, RefreshCw } from "lucide-react";
import { ToastContainer } from "react-toastify";
import useEmployers from "hooks/useEmployers";
import { Employer } from "services/employer.service";

const Employers = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const { data: response, isLoading, isError, refetch } = useEmployers({ page, limit: 20 });

  const employers: Employer[] = response?.data?.data ?? [];
  const total = response?.data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);

  const filtered = search
    ? employers.filter((e) => {
        const q = search.toLowerCase();
        return (
          e.user?.firstName?.toLowerCase().includes(q) ||
          e.user?.lastName?.toLowerCase().includes(q) ||
          e.user?.email?.toLowerCase().includes(q) ||
          e.user?.phoneNumber?.includes(q)
        );
      })
    : employers;

  return (
    <React.Fragment>
      <BreadCrumb title="Employers" pageTitle="Users" />
      <ToastContainer closeButton={false} limit={1} />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-2.5 size-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter this page by name, email or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-gray-400"
            />
          </div>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="size-4" /> Refresh
          </button>
          <span className="sm:ml-auto text-sm text-gray-500">{total} total</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table data-stack className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Employer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Location</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Gender</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">KYC Doc</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <ErrorRow colSpan={6} label="Couldn't load employers" onRetry={refetch} />
              ) : isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <UserCheck className="size-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No employers found</p>
                  </td>
                </tr>
              ) : (
                filtered.map((emp) => (
                  <tr key={emp._id} className="hover:bg-gray-50 transition-colors">
                    <td data-label="Employer" data-cell="title" className="px-4 py-3">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        {emp.profilePicture?.url ? (
                          <img
                            src={emp.profilePicture.url}
                            alt=""
                            className="size-8 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="size-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-sm flex-shrink-0">
                            {emp.user?.firstName?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">
                            {emp.user?.firstName} {emp.user?.lastName}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td data-label="Contact" data-cell="block" className="px-4 py-3">
                      <p className="text-gray-700">{emp.user?.email}</p>
                      <p className="text-xs text-gray-400">{emp.user?.phoneNumber}</p>
                    </td>
                    <td data-label="Location" className="px-4 py-3 text-gray-600">
                      {[emp.residenceLga || emp.lga, emp.residenceState || emp.state]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </td>
                    <td data-label="Gender" className="px-4 py-3 text-gray-600 capitalize">
                      {emp.gender || "—"}
                    </td>
                    <td data-label="KYC Doc" className="px-4 py-3">
                      {emp.docType ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {emp.docType}
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                          Pending
                        </span>
                      )}
                    </td>
                    <td data-label="Joined" className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {new Date(emp.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages} · {total} total
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default Employers;
